import { create } from 'zustand';

import { getServiceId } from '@/api/tokens.helper';
import { useActiveAccount } from '@/store/active-account/active-account.store';
import type { PersonRole } from '@/store/persons/person.dto';
import { getVesselsByIds } from '@/store/vessels/vessel.api';
import { useVessels } from '@/store/vessels/vessels.store';

type CurrentServiceProviderState = {
  selectedServiceProviderId?: string;
  selectServiceProvider: (id: string) => void;
  clearSelectedServiceProvider: () => void;
};

export const useCurrentServiceProvider = create<CurrentServiceProviderState>(() => ({
  selectServiceProvider: (id) => {
    useActiveAccount.getState().setActiveAccount(id, 'service-provider');
  },
  clearSelectedServiceProvider: () => {
    if (useActiveAccount.getState().serviceType === 'service-provider') {
      useActiveAccount.getState().clearActiveAccount();
    }
  },
}));

/**
 * Which service the app is looking at, decided from the session alone.
 *
 * Split out of `initActiveAccount` because this half is synchronous and the vessel list
 * there is not. Every route guard asks whether the viewer owns the *selected vessel*, and
 * an account that has not been resolved yet answers "no" — which the guard turns into a
 * redirect it cannot take back. So this has to settle before the first route renders,
 * without waiting on a vessel fetch that has nothing to do with the question.
 *
 * `me()` always leaves a valid service id behind (see `normalizeSession`), so the id is
 * already there to be matched by the time this runs.
 */
export const resolveActiveAccount = (services: PersonRole[]) => {
  const activeId = getServiceId();
  const serviceType = services.find((s) => s.service_id === activeId)?.service_type;

  if (serviceType === 'vessel' || serviceType === 'service-provider') {
    useActiveAccount.getState().setActiveAccount(activeId, serviceType);
  }
};

export const initActiveAccount = async (services: PersonRole[]) => {
  resolveActiveAccount(services);

  const vesselIds = services.filter((s) => s.service_type === 'vessel').map((s) => s.service_id);
  const vessels = vesselIds.length ? await getVesselsByIds(vesselIds) : [];

  useVessels.setState({ myVessels: vessels });
};
